export type SignalHint = {
  id: string;
  createdAt: string;
  sourceContributionId?: string | null;
  canId: number;
  canIdHex: string;
  signalName: string;
  description: string;
  dataPattern: string;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleYear?: number | null;
  vehicleBrand?: string | null;
  vehicleHint?: string | null;
  messageName?: string | null;
  startBit?: number | null;
  length?: number | null;
  endianness?: string | null;
  factor?: number | null;
  offset?: number | null;
  unit?: string | null;
  category?: string | null;
  usableForAutoMatch?: boolean;
  needsRealValidation?: boolean;
  status: string;
  source: string;
  confidence: number;
  confidenceLabel?: string | null;
  sourceFile?: string | null;
  sourceCommit?: string | null;
  sourceSignalName?: string | null;
};

export type SignalHintFilters = {
  canId?: number | string;
  make?: string;
  model?: string;
  year?: number | string;
  limit?: number;
};

const responseCache = new Map<string, Promise<SignalHint[]>>();
const canIdCache = new Map<number, Promise<SignalHint[]>>();
const SIGNAL_HINT_LOOKUP_CONCURRENCY = 6;
const HINT_SOURCE_PRIORITY: Record<string, number> = {
  community_verified: 0,
  opendbc_seed: 1,
  tesla_repo: 2,
  bmw_repo: 2,
  mazda_repo: 2
};

export function parseCanIdValue(value: number | string): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 0x1fffffff) return value;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/^0x/i, '');
  const radix = trimmed.toLowerCase().startsWith('0x') ? 16 : 10;
  if (radix === 16 && !/^[0-9a-fA-F]{1,8}$/.test(normalized)) return null;
  if (radix === 10 && !/^\d+$/.test(normalized)) return null;
  const parsed = Number.parseInt(normalized, radix);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 0x1fffffff ? parsed : null;
}

export function formatCanIdHex(canId: number) {
  return `0x${canId.toString(16).toUpperCase().padStart(canId > 0x7ff ? 8 : 3, '0')}`;
}

function cacheKey(filters: SignalHintFilters) {
  const params = new URLSearchParams();
  params.set('limit', String(filters.limit ?? 100));
  if (filters.canId !== undefined) {
    const canId = parseCanIdValue(filters.canId);
    if (canId !== null) params.set('can_id', formatCanIdHex(canId));
  }
  if (filters.make) params.set('make', filters.make.trim());
  if (filters.model) params.set('model', filters.model.trim());
  if (filters.year !== undefined && String(filters.year).trim()) params.set('year', String(filters.year).trim());
  return `/api/decode/signals?${params.toString()}`;
}

function normalizeHint(value: SignalHint): SignalHint {
  const canId = parseCanIdValue(value.canId) ?? parseCanIdValue(value.canIdHex) ?? 0;
  return {
    ...value,
    canId,
    canIdHex: value.canIdHex || formatCanIdHex(canId),
    confidence: typeof value.confidence === 'number' ? value.confidence : Number(value.confidence) || 0
  };
}

export async function fetchSignalHints(filters: SignalHintFilters = {}) {
  const key = cacheKey(filters);
  if (!responseCache.has(key)) {
    responseCache.set(key, fetch(key)
      .then(async (response) => {
        const payload = await response.json() as { ok?: boolean; signals?: SignalHint[]; error?: string };
        if (!response.ok || !payload.ok) throw new Error(payload.error || 'Signal hints unavailable.');
        return Array.isArray(payload.signals) ? payload.signals.map(normalizeHint) : [];
      })
      .catch((error) => {
        responseCache.delete(key);
        throw error;
      }));
  }

  return responseCache.get(key) as Promise<SignalHint[]>;
}

export async function fetchSignalHintsForCanId(canId: number | string) {
  const parsed = parseCanIdValue(canId);
  if (parsed === null) return [];
  if (!canIdCache.has(parsed)) {
    canIdCache.set(parsed, fetchSignalHints({ canId: parsed, limit: 50 })
      .catch(() => []));
  }

  return canIdCache.get(parsed) as Promise<SignalHint[]>;
}

export async function fetchSignalHintsForCanIds(canIds: Array<number | string>, maxLookups = 80) {
  const uniqueIds = Array.from(new Set(canIds
    .map(parseCanIdValue)
    .filter((value): value is number => value !== null)))
    .slice(0, maxLookups);

  if (uniqueIds.length === 0) return [];
  const results: SignalHint[] = [];
  let cursor = 0;
  const workerCount = Math.min(SIGNAL_HINT_LOOKUP_CONCURRENCY, uniqueIds.length);

  await Promise.all(Array.from({ length: workerCount }, async () => {
    while (cursor < uniqueIds.length) {
      const canId = uniqueIds[cursor];
      cursor += 1;
      const hints = await fetchSignalHintsForCanId(canId);
      results.push(...hints);
    }
  }));

  return results;
}

export function groupSignalHintsByCanId(hints: SignalHint[]) {
  return hints.reduce<Record<number, SignalHint[]>>((groups, hint) => {
    if (isSmokeTestSignalHint(hint)) return groups;
    const canId = parseCanIdValue(hint.canId) ?? parseCanIdValue(hint.canIdHex);
    if (canId === null) return groups;
    groups[canId] = [...(groups[canId] ?? []), hint];
    return groups;
  }, {});
}

export function isSmokeTestSignalHint(hint: SignalHint) {
  const text = [
    hint.signalName,
    hint.description,
    hint.sourceSignalName
  ].filter(Boolean).join(' ').toLowerCase();
  return text.includes('smoke test');
}

export function sortSignalHintsByPriority(hints: SignalHint[]) {
  return [...hints].sort((a, b) => {
    const priorityA = HINT_SOURCE_PRIORITY[a.source] ?? 9;
    const priorityB = HINT_SOURCE_PRIORITY[b.source] ?? 9;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return (b.confidence ?? 0) - (a.confidence ?? 0) || a.signalName.localeCompare(b.signalName);
  });
}

export function groupedVisibleSignalHints(hints: SignalHint[], limitPerCanId = 3) {
  const groups = groupSignalHintsByCanId(hints.filter((hint) => !isSmokeTestSignalHint(hint)));
  return Object.entries(groups)
    .map(([canId, groupHints]) => {
      const sorted = sortSignalHintsByPriority(groupHints);
      return {
        canId: Number(canId),
        canIdHex: sorted[0]?.canIdHex ?? formatCanIdHex(Number(canId)),
        hints: sorted.slice(0, limitPerCanId),
        hiddenCount: Math.max(0, sorted.length - limitPerCanId)
      };
    })
    .sort((a, b) => a.canId - b.canId);
}

export function vehicleContextLabel(hint: SignalHint) {
  return [
    hint.vehicleYear,
    hint.vehicleMake ?? hint.vehicleBrand,
    hint.vehicleModel ?? hint.vehicleHint
  ].filter(Boolean).join(' ');
}

export function signalHintSourceLabel(hint: SignalHint) {
  if (hint.source === 'opendbc_seed') return 'Likely signal (opendbc)';
  if (['tesla_repo', 'bmw_repo', 'mazda_repo'].includes(hint.source)) return 'Possible signal (community data)';
  if (hint.source === 'community_verified') return 'Verified signal';
  return hint.source.replace(/_/g, ' ');
}

export function signalHintBadgeLabel(hint: SignalHint) {
  if (hint.source === 'community_verified') return 'Verified signal';
  if (hint.source === 'opendbc_seed') return 'Likely signal';
  if (['tesla_repo', 'bmw_repo', 'mazda_repo'].includes(hint.source)) return 'Possible signal';
  return 'Signal hint';
}
